import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Table,
    Button,
    ActionButton,
    Delete,
    AddFill
} from 'components';
import { CheckFill, CrossFill } from 'components/icons';
import { useTable, useBoolean } from 'hooks';
import { Transformation } from 'types/transformation';
import { useBlockNavigation } from 'providers';
import AssignTransformationsDialog from '../AssignTransformations.dialog';

interface AssignedTransformationsProps {
    transformations: Transformation[];
    setTransformationsToAssign: Dispatch<SetStateAction<Transformation[]>>;
    assignedTransformations: Transformation[];
    isReadOnly: boolean;
}

export const TransformationsTab = styled.div`
    padding: 20px 0;
`;

const AssignedTransformations: FC<AssignedTransformationsProps> = ({
    transformations,
    setTransformationsToAssign,
    assignedTransformations,
    isReadOnly
}) => {
    const { t } = useTranslation();
    const [isAssignModalOpen, showAssignModal, hideAssignModal] = useBoolean();
    const { setIsNavigationBlocked } = useBlockNavigation();
    const table = useTable<Transformation>({
        columns: useMemo(
            () => [
                {
                    Header: t('general.transformation_pattern'),
                    accessor: 'pattern'
                },
                {
                    Header: t('general.transformation_replacement'),
                    accessor: 'replacement'
                },
                {
                    Header: t('general.transformation_case_insensitive'),
                    accessor: 'caseInsensitive',
                    Cell: (row) =>
                        row.value ? (
                            <CheckFill color="primary" />
                        ) : (
                            <CrossFill color={['grey', 'A200']} />
                        )
                }
            ],
            [t]
        ),
        data: transformations,
        hasSelect: true
    });

    const deleteSelected = useCallback(() => {
        setIsNavigationBlocked(true);
        const idsToDelete = table.selectedFlatRows.map(
            ({ original }) => original.id
        );

        setTransformationsToAssign(
            transformations.filter(
                (transformation) => !idsToDelete.includes(transformation.id)
            )
        );
    }, [
        setIsNavigationBlocked,
        setTransformationsToAssign,
        transformations,
        table.selectedFlatRows
    ]);

    return (
        <>
            <AssignTransformationsDialog
                setTransformationsToAssign={setTransformationsToAssign}
                isOpen={isAssignModalOpen}
                close={hideAssignModal}
                assignedTransformations={assignedTransformations}
            />
            <TransformationsTab>
                <Box my={4}>
                    <Typography variant="body2" component="span">
                        {t('expressions.to_create_transformation')}
                    </Typography>
                    <Link to="/transformations">
                        <Typography variant="body2" component="span">
                            {t('expressions.this_page')}
                        </Typography>
                    </Link>
                </Box>
                <Box my={4}>
                    <Button
                        isDisabled={isReadOnly}
                        icon={<AddFill />}
                        onClick={showAssignModal}>
                        {t('expressions.add_item')}
                    </Button>
                    <ActionButton
                        isDisabled={
                            !table.selectedFlatRows.length || isReadOnly
                        }
                        icon={
                            <Delete
                                color={
                                    !table.selectedFlatRows.length || isReadOnly
                                        ? 'disabled'
                                        : 'error'
                                }
                            />
                        }
                        onClick={deleteSelected}>
                        {t('expressions.delete_selected')}
                    </ActionButton>
                </Box>
                <Table controller={table} disablePagination />
            </TransformationsTab>
        </>
    );
};

export default AssignedTransformations;
